<template>
  <div>
    <div v-if="$apollo.loading">
      <spinner />
    </div>
    <div v-else>
      <div class="invite-wrapper">
        <form v-scroll-to-error @submit.prevent="submitForm">
          <v-flex class="title-screen" xs12>
            <span>{{ adminText('workflowsAll.edit_invite_information_1e86564', 'Edit invite information') }}</span>
          </v-flex>
          <v-flex x4>
            <div class="title-description screen-description">
              {{ adminText('workflowsAll.rename_fields_change_titles_and_description_f_1869789', 'Rename fields. Change titles and description for every step in registration form.') }}
            </div>
          </v-flex>
          <div column justify-start class="container">
            <v-tabs v-model="tab" slider-color="transparent" class="tabs">
              <v-tab class="tab">{{ adminText('workflowsAll.register_0391d22', 'REGISTER') }}</v-tab>
              <v-tab v-if="hasWorkshops" class="tab">{{ adminText('workflowsAll.workshops_e12c846', 'WORKSHOPS') }}</v-tab>
              <v-tab class="tab">{{ adminText('workflowsAll.registered_5d1c39f', 'REGISTERED') }}</v-tab>
              <v-tab class="tab">{{ adminText('workflowsAll.closed_be3e73b', 'CLOSED') }}</v-tab>
              <v-tab-item>
                <register-form
                  :initial-values="attendeeForm"
                  :has-categories="hasCategories"
                  :has-workshops="hasWorkshops"
                  :vs="$v.form.attendeeForm"
                  @values="form.attendeeForm = $event"
                />
              </v-tab-item>
              <v-tab-item v-if="hasWorkshops">
                <workshops-form
                  :initial-values="workshopsForm"
                  :vs="$v.form.workshopsForm"
                  @values="form.workshopsForm = $event"
                />
              </v-tab-item>
              <v-tab-item>
                <registered-form
                  :initial-values="base"
                  :vs="$v.form.base"
                  @values="form.base = { ...form.base, ...$event }"
                />
              </v-tab-item>
              <v-tab-item>
                <closed-form
                  :initial-values="base"
                  :vs="$v.form.base"
                  @values="form.base = { ...form.base, ...$event }"
                />
              </v-tab-item>
            </v-tabs>
            <div class="update-button">
              <main-button depressed @click="$emit('submit')"> {{ adminText('workflowsAll.update_fb91e24', 'Update') }} </main-button>
            </div>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import WorkflowDisplay from '@/admin-i18n/workflows-all-display'
import { required, requiredIf } from 'vuelidate/lib/validators'
import Spinner from '../../shared/spinner'
import { submitGuard } from '../../../utils'
import { GET_EVENT_DETAILS } from '../../../graphql/event.graphql'
import { SET_INVITE_FORM_STYLES } from '../../../graphql/utils.graphql'
import RegisterForm from './edit-stepper/register-form'
import WorkshopsForm from './edit-stepper/workshops-form'
import RegisteredForm from './edit-stepper/registered-form'
import ClosedForm from './edit-stepper/closed-form'

export default {
  mixins: [WorkflowDisplay],
  name: 'invite-edit-container',
  components: {
    Spinner,
    RegisterForm,
    WorkshopsForm,
    RegisteredForm,
    ClosedForm,
  },
  props: {
    eventId: String,
  },
  data() {
    return {
      currentSite: 'register',
      hasWorkshops: false,
      hasCategories: false,
      form: {
        base: {},
        footer: {},
        attendeeForm: {},
        workshopsForm: {},
      },
      base: null,
      attendeeForm: null,
      workshopsForm: null,
      tab: null,
    }
  },
  methods: {
    isPressed(siteName) {
      return this.currentSite === siteName
    },
    changeButton(siteName) {
      this.currentSite = siteName
    },
    async updateInviteFormStyles() {
      return this.$apollo.mutate({
        mutation: SET_INVITE_FORM_STYLES,
        variables: {
          eventId: this.eventId,
          input: this.form,
        },
      })
    },
    async submitForm() {
      await submitGuard(
        this,
        async () => {
          await this.updateInviteFormStyles()
          this.goTo(this.routes.organizer.inviteAttendee)
        },
        instance => this.goToError(instance),
      )
    },
    goToError(instance) {
      const screenMappings = {
        attendeeForm: 0,
        workshopsForm: 1,
      }
      const {
        $v: { form },
      } = instance
      for (const key of Object.keys(form).filter(el => !el.startsWith('$'))) {
        for (const property of Object.keys(form[key]).filter(el => !el.startsWith('$'))) {
          if (form[key][property]['$invalid']) {
            this.tab =
              key !== 'base'
                ? screenMappings[key]
                : this.hasWorkshops
                  ? property === 'eventClosed'
                    ? 3
                    : 2
                  : property === 'eventClosed'
                    ? 2
                    : 1
            break
          }
        }
      }
    },
  },
  validations() {
    return {
      form: {
        attendeeForm: {
          title: { required },
          titleDescription: { required },
          form: {
            // firstName: { required },
            // lastName: { required },
            category: { required },
            zones: { required },
          },
        },
        workshopsForm: {
          apply: { required: requiredIf(() => this.hasWorkshops) },
          titleDescription: { required: requiredIf(() => this.hasWorkshops) },
          snackbarText: {
            required: requiredIf(() => this.form.workshopsForm.required && this.hasWorkshops),
          },
        },
        base: {
          submitNote: { required },
          submitInfo: { required },
          eventClosed: { required },
        },
      },
    }
  },
  apollo: {
    getEventDetails: {
      query: GET_EVENT_DETAILS,
      variables() {
        return {
          eventId: this.eventId,
          status: 'ALLOWED',
        }
      },
      manual: true,
      result({ data, loading }) {
        if (!loading && data) {
          const {
            getEvent: {
              extraModules: { workshops, categoriesAndZones },
              eventUtils,
            },
          } = data
          if (eventUtils && eventUtils.inviteFormStyles) {
            this.form = eventUtils.inviteFormStyles
          }
          const { base, attendeeForm, workshopsForm } = this.form
          this.base = base ? base : null
          this.attendeeForm = attendeeForm ? attendeeForm : null
          this.workshopsForm = workshopsForm ? workshopsForm : null
          this.hasWorkshops = workshops
          this.hasCategories = categoriesAndZones
        }
      },
    },
  },
}
</script>

<style scoped>
.container {
  margin: 0;
  padding-left: 0;
  padding-right: 0;
}
.invite-wrapper {
  max-width: 672px;
}

.tab {
  font-family: 'Raleway', sans-serif;
  font-style: normal;
  font-size: 16px;
  font-weight: bold;
  line-height: 120%;
  text-transform: uppercase;
  overflow-x: auto;
}

.tab >>> .v-tabs__item {
  padding: 1rem;
  color: #0277bd;
}

.tab >>> .v-tabs__item--active {
  background: #0277bd;
  border-radius: 6px;
  color: white;
}

.tabs >>> .v-tabs__container {
  flex: 0 1 auto;
  height: 2.375rem;
  background: #f5f5f5;
  border-radius: 6px;
}

>>> .title-description {
  margin-bottom: 0;
}
.title-screen {
  font-family: 'Raleway';
  font-style: normal;
  font-weight: 600;
  font-size: 24px;
  letter-spacing: -0.03em;
  font-feature-settings: 'pnum' on, 'lnum' on;
  color: #ff4c00;
}
.update-button {
  display: flex;
  justify-content: flex-end;
}
@media screen and (max-width: 800px) {
  .title-screen {
    display: none;
  }
  .invite-wrapper {
    margin-top: 0.5rem;
  }
}
</style>
